import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  FilterBar,
  FilterSelect,
  InlineStats,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import { IconMerchants, IconRefresh } from '../../components/admin/icons';
import {
  formatDate,
  formatNumber,
  subscriptionStatusLabels,
  subscriptionStatusTone
} from '../../components/admin/labels';
import { billingLabel, billingTone, pilotPreviouslyUsed } from '../../components/admin/plans';

const billingModelOptions = [
  { value: 'subscription', label: 'Merchant subscription' },
  { value: 'courier', label: 'Courier subscription' },
  { value: 'pay_per_case', label: 'Pay per case' }
];

/**
 * Everyone currently on paid billing — merchants and couriers alike, because
 * "who is paying us" is one question — with plan, Stripe
 * status, usage against allowance, next renewal, outstanding cancellation
 * requests, and cancelling. Distinct from Merchant Management, which covers every merchant
 * (including free/pilot) and every editable detail; this is the billing-
 * focused view the admin panel didn't have at all before.
 */
const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingModelFilter, setBillingModelFilter] = useState('');
  const [actingId, setActingId] = useState(null);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/subscriptions');
      setMerchants(data || []);
    } catch (error) {
      toast('Could not load subscriptions.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const filtered = billingModelFilter
    ? merchants.filter((m) =>
        billingModelFilter === 'courier'
          ? m.accountKind === 'courier'
          : m.accountKind !== 'courier' && m.billingModel === billingModelFilter
      )
    : merchants;

  /**
   * The two sides bill through different endpoints but behave identically
   * from here, so the row's accountKind picks the URL and nothing else in
   * this screen has to care which it is.
   */
  const isCourier = (row) => row.accountKind === 'courier';
  const cancelUrl = (row) =>
    isCourier(row)
      ? `/api/admin/courier-accounts/${row._id}/cancel-subscription`
      : `/api/admin/merchants/${row._id}/subscription/cancel`;
  const declineUrl = (row) =>
    isCourier(row)
      ? `/api/admin/courier-accounts/${row._id}/decline-cancellation`
      : `/api/admin/merchants/${row._id}/cancellation-request/decline`;
  const detailPath = (row) =>
    isCourier(row)
      ? `/admin/courier-accounts?q=${encodeURIComponent(row.email || row.name || '')}`
      : `/admin/merchants/${row._id}`;

  const runAction = async (row, successMessage, body) => {
    setActingId(row._id);
    try {
      await api.post(cancelUrl(row), body);
      toast(successMessage, { tone: 'success' });
      fetchSubscriptions();
    } catch (error) {
      toast(error.response?.data?.error || 'Could not cancel this subscription.', { tone: 'error' });
    } finally {
      setActingId(null);
    }
  };

  const handleCancel = async (merchant) => {
    const ok = await confirm({
      title: 'Cancel this subscription?',
      message: `This ends ${merchant.name}'s Stripe subscription immediately, not at the next renewal. They'll be asked to choose a plan again before taking further action.`,
      tone: 'danger',
      confirmLabel: 'Cancel subscription'
    });
    if (!ok) return;
    await runAction(merchant, 'Subscription canceled.');
  };

  /**
   * Carrying out what a merchant asked for. `atPeriodEnd` leaves them running
   * until the period they have already paid for is up, which is what most
   * requests ask for — cancelling outright there would take away access they
   * have been billed for.
   */
  const handleCancelAtPeriodEnd = async (merchant) => {
    const endsOn = merchant.subscription?.currentPeriodEnd
      ? formatDate(merchant.subscription.currentPeriodEnd)
      : 'the end of the current period';
    const ok = await confirm({
      title: 'Cancel at period end?',
      message: `${merchant.name}'s subscription will stop renewing and end on ${endsOn}. They keep full access until then.`,
      confirmLabel: 'Schedule cancellation'
    });
    if (!ok) return;
    await runAction(merchant, 'Cancellation scheduled for the end of the period.', {
      atPeriodEnd: true
    });
  };

  const handleDeclineRequest = async (merchant) => {
    const ok = await confirm({
      title: 'Decline this request?',
      message: `Closes ${merchant.name}'s cancellation request without cancelling anything. Use this once you've spoken to them and they're staying.`,
      confirmLabel: 'Decline request'
    });
    if (!ok) return;

    setActingId(merchant._id);
    try {
      await api.post(declineUrl(merchant));
      toast('Request declined — nothing was cancelled.', { tone: 'success' });
      fetchSubscriptions();
    } catch (error) {
      toast(error.response?.data?.error || 'Could not update this request.', { tone: 'error' });
    } finally {
      setActingId(null);
    }
  };

  const pendingRequests = merchants.filter((m) => m.cancellationRequest?.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Merchants can't cancel themselves — they raise a request and it
          lands here, so someone sees the reason and has a chance to save the
          account before anything is switched off. */}
      {pendingRequests.length > 0 && (
        <Card>
          <CardHeader
            title={`Cancellation requests (${pendingRequests.length})`}
            description="Customers waiting on a decision. Nothing is cancelled until you act here."
          />
          <div className="divide-y divide-ink-200">
            {pendingRequests.map((merchant) => (
              <div key={merchant._id} className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => navigate(detailPath(merchant))}
                      className="rounded text-left font-600 text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                    >
                      {merchant.name}
                    </button>
                    <p className="mt-0.5 text-sm text-ink-500">{merchant.email}</p>
                    <p className="mt-2 text-sm text-ink-600">
                      Asked to cancel{' '}
                      <span className="font-600 text-ink-900">
                        {merchant.cancellationRequest.timing === 'immediate'
                          ? 'as soon as possible'
                          : 'at the end of the billing period'}
                      </span>
                      {merchant.cancellationRequest.requestedAt &&
                        ` on ${formatDate(merchant.cancellationRequest.requestedAt)}`}
                      .
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={actingId === merchant._id}
                      onClick={() => handleDeclineRequest(merchant)}
                    >
                      Decline
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={actingId === merchant._id}
                      onClick={() => handleCancelAtPeriodEnd(merchant)}
                    >
                      Cancel at period end
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={actingId === merchant._id}
                      onClick={() => handleCancel(merchant)}
                    >
                      Cancel now
                    </Button>
                  </div>
                </div>

                {merchant.cancellationRequest.note && (
                  <blockquote className="mt-3 whitespace-pre-wrap rounded-xl bg-ink-50 px-4 py-3 text-sm leading-relaxed text-ink-700">
                    {merchant.cancellationRequest.note}
                  </blockquote>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <FilterBar
          actions={
            <>
              <InlineStats
                items={[
                  {
                    label: 'On paid billing',
                    value: loading ? '—' : formatNumber(filtered.length)
                  }
                ]}
              />
              <Button variant="secondary" size="sm" onClick={fetchSubscriptions} loading={loading}>
                <IconRefresh size={15} />
                Refresh
              </Button>
            </>
          }
        >
          <FilterSelect
            label="Billing"
            value={billingModelFilter}
            onChange={(e) => setBillingModelFilter(e.target.value)}
          >
            <option value="">All models</option>
            {billingModelOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </FilterSelect>
        </FilterBar>

        {loading ? (
          <TableSkeleton rows={6} cols={7} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconMerchants}
            title="No paid merchants yet"
            description="Merchants show up here once they subscribe or activate pay-per-case."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th>Plan</Th>
                <Th>Stripe status</Th>
                <Th>Usage this cycle</Th>
                <Th>Next renewal</Th>
                <Th>Started</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((merchant) => {
                const hasStripeSubscription = Boolean(merchant.subscription?.stripeSubscriptionId);
                const isCanceled = merchant.subscription?.status === 'canceled';
                return (
                  <Tr key={merchant._id}>
                    <Td>
                      <button
                        type="button"
                        onClick={() => navigate(detailPath(merchant))}
                        className="rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-600 text-brand-700 hover:underline">{merchant.name}</span>
                          <StatusPill tone={isCourier(merchant) ? 'info' : 'neutral'} dot={false}>
                            {isCourier(merchant) ? 'Courier' : 'Merchant'}
                          </StatusPill>
                        </span>
                        <span className="block text-xs text-ink-500">{merchant.email}</span>
                      </button>
                    </Td>
                    <Td>
                      {isCourier(merchant) ? (
                        <StatusPill tone="success">Intelligence</StatusPill>
                      ) : (
                        <>
                          <StatusPill tone={billingTone(merchant)}>{billingLabel(merchant)}</StatusPill>
                          {pilotPreviouslyUsed(merchant) && (
                            <span className="mt-1 block text-xs text-ink-500">Pilot used</span>
                          )}
                        </>
                      )}
                    </Td>
                    <Td>
                      {merchant.billingModel === 'pay_per_case' ? (
                        <span className="text-ink-500">—</span>
                      ) : (
                        <>
                          <StatusPill tone={subscriptionStatusTone[merchant.subscription?.status] || 'neutral'}>
                            {subscriptionStatusLabels[merchant.subscription?.status] || merchant.subscription?.status || '—'}
                          </StatusPill>
                          {merchant.subscription?.paused && (
                            <span className="mt-1 block text-xs font-600 text-warning-700">Billing paused</span>
                          )}
                          {merchant.subscription?.cancelAt && (
                            <span className="mt-1 block text-xs font-600 text-danger-700">
                              Ends {formatDate(merchant.subscription.cancelAt)}
                            </span>
                          )}
                          {merchant.cancellationRequest?.status === 'pending' && (
                            <span className="mt-1 block text-xs font-600 text-warning-700">
                              Cancellation requested
                            </span>
                          )}
                        </>
                      )}
                    </Td>
                    <Td>
                      {isCourier(merchant) ? (
                        // A courier subscription is flat — no seats, no case
                        // allowance, nothing to count against.
                        <span className="text-ink-500">Unlimited</span>
                      ) : merchant.billingModel === 'pay_per_case' ? (
                        <span className="tabular-nums text-ink-900">
                          {formatNumber(merchant.caseCountThisCycle ?? 0)} case
                          {merchant.caseCountThisCycle === 1 ? '' : 's'} paid
                        </span>
                      ) : merchant.includedCases != null ? (
                        <span className="tabular-nums text-ink-900">
                          {formatNumber(merchant.caseCountThisCycle ?? 0)} / {formatNumber(merchant.includedCases)}
                        </span>
                      ) : merchant.billingModel === 'subscription' ? (
                        <span className="tabular-nums text-ink-900">
                          {formatNumber(merchant.caseCountThisCycle ?? 0)} <span className="text-ink-500">/ Unlimited</span>
                        </span>
                      ) : (
                        <span className="text-ink-500">—</span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-ink-500">
                      {merchant.subscription?.currentPeriodEnd
                        ? formatDate(merchant.subscription.currentPeriodEnd)
                        : '—'}
                    </Td>
                    <Td className="whitespace-nowrap text-ink-500">
                      {formatDate(merchant.createdAt)}
                    </Td>
                    <Td className="text-right">
                      {hasStripeSubscription && !isCanceled ? (
                        <div className="flex justify-end gap-1.5">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="whitespace-nowrap"
                            loading={actingId === merchant._id}
                            disabled={actingId != null}
                            onClick={() => handleCancelAtPeriodEnd(merchant)}
                          >
                            Cancel at period end
                          </Button>
                          <Button
                            variant="dangerGhost"
                            size="sm"
                            loading={actingId === merchant._id}
                            disabled={actingId != null}
                            onClick={() => handleCancel(merchant)}
                          >
                            Cancel now
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-500">
                          {isCanceled ? 'Canceled' : 'No Stripe subscription'}
                        </span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionManagement;
